import { Fragment, ChangeEvent, FC, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Types
import QuestionTypes from 'types/QuestionTypes';
// Models
import IQuiz from 'models/Quiz';
// Actions
import { setAdditionalAnswers } from 'store/quiz/quizSlice';
// Selectors
import { selectQuiz, selectAdditionalQuestions } from 'store/quiz/quizSelectors';
import { selectPage } from 'store/pages/pagesSelectors';
// Components
import { Button } from 'components/Buttons';
import { Input, Select } from 'components/Controls';
import AppBar from 'components/AppBar';
import PoweredBy from 'components/PoweredBy';

const AdditionalQuestions:FC = () => {
  const { t } = useTranslation();

  const navigateTo = useNavigateTo();
  
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const additionalQuestions:any = useAppSelector(selectAdditionalQuestions);
  const page = useAppSelector(selectPage);

  const [ answers, setAnswers ] = useState<any>(additionalQuestions);

  const handleClickButton = (questionRef:string, answerRef:string) => {
    setAnswers((prevState:any) => {
      const question = prevState[questionRef];
      return {
        ...prevState,
        [questionRef]: {
          ...question,
          answers: Object.keys(question.answers).reduce((acc:any, key) => {
            const answer = question.answers[key];
            if ( question.type === QuestionTypes.SingleChoice ){
              acc[key] = { ...answer, checked: key === answerRef ? !answer.checked : false };
            } else {
              acc[key] = { ...answer, checked: key === answerRef ? !answer.checked : answer.checked };
            }
            return acc;
          }, {})
        }
      };
    })
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setAnswers((prevState:any) => {
      return {...prevState, [name]: {
        ...prevState[name],
        text: value
      }};
    })
  }

  const handleChangeSelect = (event:ChangeEvent<HTMLSelectElement>) => {
    const { name:questionRef, value:answerRef } = event.target;
    setAnswers((prevState:any) => {
      const question = prevState[questionRef];
      return {
        ...prevState,
        [questionRef]: {
          ...question,
          answerRef
        }
      };
    })
  }

  const handleClick = async () => {
    if ( !quiz ) return;

    const result = [];
    for ( let key in answers ){
      const question = answers[key];
      const questionData = { ref: key, title: question.title };
      if ( question.type === QuestionTypes.ShortText ){
        result.push({
          ref: question.answerRef,
          text: question.text,
          question: questionData
        });
      } else if (question.type === QuestionTypes.SingleChoice && question.renderAsDropdown) {
        result.push({
          ref: question.answerRef,
          title: question.answers[question.answerRef].title,
          question: questionData
        });
      } else {
        for ( let k in question.answers ){
          const answer = question.answers[k];
          if ( answer.checked ){
            result.push({
              ref: k,
              title: answer.title,
              question: questionData
            });
          }
        }
      }
    }

    dispatch(setAdditionalAnswers(result));
    navigateTo('pages-handler');
  }

  const isButtonDisabled = () => {
    let disabled = false;
    for ( let key in answers ){
      const question = answers[key];
      if ( question.type === QuestionTypes.ShortText ){
        if ( !question.text ) disabled = true;
      } else if (question.type === QuestionTypes.SingleChoice && question.renderAsDropdown) {
        if ( !question.answerRef ) disabled = true;
      } else {
        const answers = question.answers;
        disabled = Object.keys(answers).every((k) => !answers[k].checked);
      }
      if ( disabled ) break;
    }
    return disabled;
  }

  if (!quiz) return null;
  return (
    <div
      className="p-4 flex flex-col w-full h-full bg-gray-200 overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <AppBar />
      <div className="flex-1 text-primary pt-4">
        {page.title ? (
          <span className="block text-primary text-lg font-bold text-center mb-4">
            {page.title}
          </span>
        ) : null}
        {Object.keys(answers).map((key) => {
          const question = answers[key];
          return (
            <div key={`pre-quiz-question-${key}`} className="mb-4">
              {question.image ? (
                <img src={question.image} alt={question.title} className="mx-auto w-full object-cover mb-4 shadow-primary rounded-lg" />
              ) : null}
              <div
                className="mb-4 p-1 shadow-primary-border rounded-xl"
                style={{ backgroundColor: quiz.theme.contentBackgroundColor }}
              >
                <h2 className="text-xl font-bold text-center">{question.title}</h2>
                {question.type === QuestionTypes.MultiChoice ? (
                  <span className="block text-primary text-xs text-center">
                    {`( ${t('pages.question.multiChoiceHint')} )`}
                  </span>
                ) : null}
              </div>
              {question.type === QuestionTypes.ShortText ? (
                <Input
                  className="bg-[#f2f2f2]"
                  name={key} id={key} placeholder="Free text"
                  value={question.text}
                  onChange={handleChange}
                />
              ) : (
                <Fragment>
                  {question.type === QuestionTypes.SingleChoice && question.renderAsDropdown ? (
                    <Select
                      id={key}
                      name={key}
                      options={[
                        { value: '', label: t('labels.chooseAnswer'), disabled: true },
                        ...Object.keys(question.answers).map((answerRef) => {
                          const answer = question.answers[answerRef];
                          return { label: answer.title, value: answerRef };
                        })
                      ]}
                      value={question.answerRef}
                      onChange={handleChangeSelect}
                    />
                  ) : (
                    <Fragment>
                      {Object.keys(question.answers).map((k) => {
                        const answer = question.answers[k];
                        return (
                          <Button
                            key={`${key}-answer-${k}`}
                            className={`
                              ${answer.checked
                                ? 'selected-answer-button'
                                : 'bg-linear text-[#282728]'
                              }
                              w-full normal-case font-bold mb-2
                            `}
                            onClick={() => handleClickButton(key, k)}
                          >
                            {answer.title}
                          </Button>
                        );
                      })}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </div>
          );
        })}
        <div className="mb-4 sticky -bottom-4">
          <Button
            className="
              w-full flex items-center justify-center gap-3
              uppercase primary-button
            "
            onClick={handleClick}
            disabled={isButtonDisabled()}
          >
            {t('buttons.submit')}
          </Button>
        </div>
      </div>
      <PoweredBy />
    </div>
  )
}

export default AdditionalQuestions;
